import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { SEO } from '../../components/SEO';
import { Footer } from '../../components/Footer';
import { useTranslate } from '../../contexts/I18nContext';

/**
 * Shared layout for legal pages (Terms, Privacy, Cookies, Legal Notice).
 * Uses a light-on-dark elegant reading layout. Always rendered with dark mode.
 */
export const LegalLayout = ({ title, lastUpdated, path, description, children }) => {
  const { translateText } = useTranslate();
  return (
    <div className="min-h-screen bg-[#0a0f1c] text-neutral-200" data-testid={`legal-page-${path?.replace(/^\//, '')}`}>
      <SEO path={path} title={title} description={description} />
      <div className="relative">
        {/* Subtle ambient backdrop */}
        <div
          className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full blur-3xl opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(0,204,102,0.5) 0%, rgba(0,204,102,0) 70%)' }}
          aria-hidden="true"
        />
        <div className="relative max-w-3xl mx-auto px-4 md:px-8 pt-10 md:pt-16 pb-20">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-[#00CC66] transition-colors mb-8"
            data-testid="legal-back-home"
          >
            <ChevronLeft className="w-4 h-4" />
            {translateText('Volver al inicio')}
          </Link>
          <header className="mb-10">
            <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight text-white">{translateText(title)}</h1>
            {lastUpdated && (
              <p className="mt-3 text-sm text-neutral-500">{translateText('Última actualización')}: {translateText(lastUpdated)}</p>
            )}
          </header>
          <article className="legal-prose">{children}</article>
        </div>
      </div>
      <Footer />
      <style>{`
        .legal-prose { line-height: 1.7; font-size: 15px; }
        .legal-prose h2 { color: #fff; font-size: 1.35rem; font-weight: 700; margin: 2.25rem 0 0.85rem; letter-spacing: -0.01em; }
        .legal-prose h3 { color: #f3f4f6; font-size: 1.05rem; font-weight: 600; margin: 1.5rem 0 0.6rem; }
        .legal-prose p { color: #cbd5e1; margin-bottom: 0.95rem; }
        .legal-prose ul { list-style: disc; padding-left: 1.3rem; margin: 0.5rem 0 1rem; color: #cbd5e1; }
        .legal-prose ol { list-style: decimal; padding-left: 1.3rem; margin: 0.5rem 0 1rem; color: #cbd5e1; }
        .legal-prose li { margin-bottom: 0.35rem; }
        .legal-prose a { color: #00CC66; text-decoration: underline; text-underline-offset: 2px; }
        .legal-prose a:hover { color: #00b85b; }
        .legal-prose strong { color: #fff; }
        .legal-prose .legal-callout {
          background: rgba(0, 204, 102, 0.06);
          border: 1px solid rgba(0, 204, 102, 0.18);
          border-radius: 0.75rem;
          padding: 1rem 1.1rem;
          margin: 1.2rem 0;
          color: #d1fae5;
          font-size: 14px;
        }
      `}</style>
    </div>
  );
};

export default LegalLayout;
